import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessengerReply } from '@/messenger/MessengerReply'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { formatDate, formatMoney } from '@/lib/format'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { Composer, NextStepBlock, RecordFeed, useComposerModes, useRecordActivity, type ActivityTarget } from './activityKit'
import { deleteContact, fetchContactCard, type ContactCard } from './contactApi'
import { fetchContacts } from './counterpartyApi'
import { CustomFieldsPanel } from './CustomFieldsPanel'
import { HistoryPanel } from './HistoryPanel'
import { EditActions } from './LeadRecordView'
import { ConfirmModal, Facts, Panel, RecordHeader, RecordTabs, StateView, type Fact, type HeaderAction, type MenuAction } from './record'
import { initials, listPath } from './recordHelpers'
import { FailureAlert, RecordFailure, RecordSkeleton, StatusBadge, copyLinkItem, failureText } from './recordKit'
import type { RecordViewProps } from './types'
import { useWriteEmail } from '@/mail/WriteEmail'
import { useCallAction } from '@/telephony/CallButton'
import { TaskProposalsPanel } from '@/ai/TaskProposals'

/**
 * Карточка контактного лица (Contact.dc.html, тикет #98).
 *
 * Шапка: аватар, ФИО, должность и компания; основное действие — позвонить на тот номер,
 * что есть (мобильный раньше рабочего), письмо и задача — второстепенные. Удаление — в «⋯»
 * за чертой с подтверждением; сервер откажет, пока человек участвует в открытых сделках,
 * и отказ показывается его словами.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * Сделки контакта (`DealOfContact`) — со стадией, суммой в валюте сделки и ожидаемой датой
 * закрытия; сервер отдаёт только сделки, видимые смотрящему (область `sales.deal`), поэтому
 * чужая сделка коллеги с тем же человеком сюда не попадает. Итога по сделкам нет: суммы
 * бывают в разных валютах, а сложить рубли с евро значит выдумать курс.
 *
 * - **Доверенности и документы контакта**, **часовой пояс**, **«Перевести в другую
 *   компанию»** и **«Объединить с дублем»** — нет ни полей, ни эндпоинтов (контрагент
 *   контакта не меняется по правилу домена).
 * - **Отправка письма из CRM** — почтовая интеграция заглушка (#56); кнопка открывает
 *   почтовый клиент (`mailto:`).
 */
export function ContactRecordView({ recordId, context, form, notices, edit }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const mail = useWriteEmail({ type: 'contact', id: recordId })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('deals')
  const [failure, setFailure] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [composerMode, setComposerMode] = useState<string | undefined>(undefined)
  const [composerFocus, setComposerFocus] = useState(0)

  const card = useQuery({
    queryKey: ['contact-card', recordId],
    queryFn: ({ signal }) => fetchContactCard(recordId, signal),
    retry: false,
  })
  const target: ActivityTarget = { type: 'contact', entity: 'sales.contact', resource: 'sales.contact', recordId }
  const { timeline, activities } = useRecordActivity(target)
  const modes = useComposerModes(target, { comment: t('record.contact.composer.comment') })
  // Through the PBX when it is connected (#122); otherwise the phone's own dialler via tel:.
  const dial = useCallAction({ type: 'contact', id: recordId }, card.data?.mobilePhone || card.data?.phone)

  const remove = useMutation({
    mutationFn: () => deleteContact(recordId),
    onSuccess: () => {
      setConfirmDelete(false)
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.contact'] })
      navigate(listPath('sales.contact', '/contacts'))
    },
    onError: (error) => {
      setConfirmDelete(false)
      setFailure(failureText(error, t('app.error')))
    },
  })

  if (card.isLoading) return <RecordSkeleton />
  if (!card.data) {
    return <RecordFailure error={card.error} onRetry={() => void card.refetch()} listPath={listPath('sales.contact', '/contacts')} />
  }
  const current = card.data
  const phone = current.mobilePhone || current.phone
  const taskAvailable = modes.some((mode) => mode.id === 'task')
  const openTask = () => {
    setTab('feed')
    setComposerMode('task')
    setComposerFocus((value) => value + 1)
  }

  const primary: HeaderAction | null = dial.action
    ? {
        ...dial.action,
        label: (
          <>
            {dial.action.label}
            {phone && <span style={{ fontWeight: 400, opacity: 0.9 }}>{phone}</span>}
          </>
        ),
      }
    : null
  const secondary: HeaderAction[] = []
  // A letter from the CRM lands in the feed with its delivery state (#117); mailto stays only
  // for whoever may not log activities, because it leaves no trace here.
  if (mail.action) secondary.push(mail.action)
  else if (current.email) {
    const email = current.email
    secondary.push({ id: 'mail', label: t('record.contact.mail'), icon: 'mail', onClick: () => window.location.assign(`mailto:${email}`) })
  }
  if (taskAvailable) secondary.push({ id: 'task', label: t('record.contact.task'), icon: 'work', onClick: openTask })

  const menu: MenuAction[] = [copyLinkItem(t, setFailure)]
  if (edit.mayUpdate) {
    menu.push({
      id: 'edit',
      label: t('card.edit'),
      icon: 'edit',
      onSelect: () => {
        setTab('details')
        edit.begin()
      },
    })
  }
  if (can(user, 'sales.contact', 'delete')) {
    menu.push({ id: 'delete', label: t('record.contact.delete'), icon: 'trash', danger: true, onSelect: () => setConfirmDelete(true) })
  }

  const counterpartyHref = current.counterparty ? `${listPath('sales.counterparty', '/counterparties')}/${current.counterparty.id}` : null
  const lastEvent = [...(timeline.data?.events ?? [])].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))[0]

  const facts: Fact[] = [
    {
      id: 'phones',
      label: t('record.contact.fact.phones'),
      value: (
        <span className="stack" style={{ gap: 0 }}>
          {current.mobilePhone && (
            <span className="t-md strong nowrap">
              {current.mobilePhone} <span className="t-xs muted" style={{ fontWeight: 400 }}>{t('record.contact.mobile')}</span>
            </span>
          )}
          {current.phone && (
            <span className="t-md nowrap">
              {current.phone} <span className="t-xs muted">{t('record.contact.work')}</span>
            </span>
          )}
          {!current.mobilePhone && !current.phone && <span className="t-md muted">{t('record.notSet')}</span>}
        </span>
      ),
    },
    {
      id: 'email',
      label: t('column.email'),
      value: <span className="t-md">{current.email ? <a href={`mailto:${current.email}`}>{current.email}</a> : t('record.notSet')}</span>,
    },
    {
      id: 'telegram',
      label: t('column.telegram'),
      value: <span className="t-md">{current.telegram ?? t('record.notSet')}</span>,
    },
    {
      id: 'last',
      label: t('record.contact.fact.last'),
      value: lastEvent ? new Date(lastEvent.at).toLocaleDateString() : '—',
      sub: lastEvent ? (lastEvent.subject ?? t(`timeline.kind.${lastEvent.kind}`)) : t('record.contact.fact.noTouches'),
    },
  ]

  const deals = (
    <Panel title={t('record.contact.deals')} count={current.deals.length} flush>
      {current.deals.length === 0 ? (
        <StateView compact kind="empty" title={t('record.contact.deals.empty')} description={t('record.contact.deals.emptyText')} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl" aria-label={t('contact.deals.title')}>
            <thead>
              <tr>
                <th>{t('record.contact.col.deal')}</th>
                <th>{t('participant.role')}</th>
                <th>{t('record.contact.col.stage')}</th>
                <th className="num">{t('record.contact.col.amount')}</th>
                <th>{t('record.contact.col.closeOn')}</th>
                <th>{t('column.status')}</th>
              </tr>
            </thead>
            <tbody>
              {current.deals.map((deal) => (
                <tr key={deal.dealId}>
                  <td className="wrap">
                    <span className="stack" style={{ gap: 0 }}>
                      <Link to={`${listPath('sales.deal', '/deals')}/${deal.dealId}`}>{deal.title}</Link>
                      <span className="mono muted t-xs">{deal.number}</span>
                    </span>
                  </td>
                  <td className="wrap">
                    <span className="stack" style={{ gap: 2, alignItems: 'flex-start' }}>
                      <span className={deal.role === 'decision_maker' ? 'badge b-accent' : 'badge'}>
                        <Icon name="user" />
                        {t(`participant.role.${deal.role}`)}
                      </span>
                      {deal.primary && <span className="t-xs muted">{t('participant.primary')}</span>}
                      {deal.note && <span className="t-xs muted">{deal.note}</span>}
                    </span>
                  </td>
                  <td>
                    {deal.stageName ? (
                      <span className={deal.stageType === 'won' ? 'badge b-success' : deal.stageType === 'lost' ? 'badge b-danger' : 'badge'}>
                        {deal.stageName}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="num">
                    {formatMoney(deal.amount, deal.currency, locale)}
                  </td>
                  <td>{deal.expectedCloseOn ? formatDate(deal.expectedCloseOn, locale) : <span className="muted">—</span>}</td>
                  <td>
                    <StatusBadge entity="sales.deal" value={deal.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )

  const feedColumn = (
    <div className="rec-col">
      <NextStepBlock
        target={target}
        activities={activities}
        missingHint={t('record.contact.next.missingHint')}
        onAssign={taskAvailable ? openTask : undefined}
      />
      {modes.length > 0 && <Composer modes={modes} mode={composerMode} onModeChange={setComposerMode} focusKey={composerFocus} />}
      {/* The answer into the client's Telegram or WhatsApp chat (#125), above the feed it lands in. */}
      <TaskProposalsPanel entityType="contact" entityId={recordId} />
      <MessengerReply entityType="contact" recordId={recordId} />
      <RecordFeed timeline={timeline} emptyTitle={t('record.contact.feed.empty')} emptyText={t('record.contact.feed.emptyText')} />
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.contacts'), to: listPath('sales.contact', '/contacts') }, { label: current.fullName }]}
        title={
          <span className="rec-title-row">
            <span className="ava-xl" aria-hidden="true">
              {initials(current.fullName)}
            </span>
            {current.fullName}
            {(current.primary ?? current.deals.some((deal) => deal.primary)) && (
              <span className="badge b-accent">
                <Icon name="check" />
                {t('record.contact.primary')}
              </span>
            )}
          </span>
        }
        meta={[
          current.jobTitle,
          current.counterparty ? (
            counterpartyHref ? (
              <Link key="cp" to={counterpartyHref} className="row" style={{ gap: 6 }}>
                <Icon name="building" size="sm" />
                {current.counterparty.name}
              </Link>
            ) : (
              current.counterparty.name
            )
          ) : null,
        ]}
        primary={primary}
        secondary={secondary}
        menu={menu}
        menuLabel={t('record.more')}
      />
      {mail.dialog}
      {dial.outcome}
      <FailureAlert text={failure} />
      {notices}
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'deals', label: t('contact.deals.title'), count: current.deals.length },
          { id: 'feed', label: t('record.feed.title'), count: timeline.data?.events.length ?? null },
          { id: 'details', label: t('record.contact.tab.details') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.contact.sections')}
      >
        {tab === 'deals' && (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">{deals}</div>
            <div className="rec-col">
              <Panel title={t('record.contact.lastEvents')}>
                <RecordFeed timeline={timeline} limit={5} emptyTitle={t('record.contact.feed.empty')} emptyText={t('record.contact.feed.emptyText')} />
              </Panel>
              {current.counterparty && <Colleagues card={current} />}
            </div>
          </div>
        )}
        {tab === 'feed' && <div style={{ paddingTop: 14 }}>{feedColumn}</div>}
        {tab === 'details' && (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">
              <Panel title={t('record.contact.details')} actions={<EditActions edit={edit} />}>
                {form}
              </Panel>
              <Panel title={t('card.tab.customFields')}>
                <CustomFieldsPanel entity={context.entity} recordId={recordId} resource={context.resource} />
              </Panel>
            </div>
            <div className="rec-col">
              <Panel title={t('record.history.changes')}>
                <HistoryPanel entity={context.entity} recordId={recordId} />
              </Panel>
            </div>
          </div>
        )}
      </RecordTabs>

      <ConfirmModal
        open={confirmDelete}
        icon="trash"
        title={t('record.contact.delete.title', { name: current.fullName })}
        confirmLabel={t('record.contact.delete')}
        pending={remove.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
      >
        <p>{t('record.contact.delete.text', { count: current.deals.length })}</p>
      </ConfirmModal>
    </div>
  )
}

/** Коллеги в той же компании — люди контрагента, кроме самого контакта. */
function Colleagues({ card }: { card: ContactCard }) {
  const { t, user } = useApp()
  const counterpartyId = card.counterparty?.id ?? ''
  const allowed = can(user, 'sales.counterparty', 'read')
  const query = useQuery({
    queryKey: ['counterparty-contacts', counterpartyId],
    queryFn: ({ signal }) => fetchContacts(counterpartyId, signal),
    enabled: allowed && counterpartyId !== '',
    retry: false,
  })
  if (!allowed) return null
  const people = (query.data ?? []).filter((person) => person.id !== card.id)
  return (
    <Panel title={t('record.contact.colleagues')} count={query.isSuccess ? people.length : null}>
      {query.isLoading ? (
        <p className="muted">{t('app.loading')}</p>
      ) : query.isError ? (
        <StateView compact kind="error" title={t('record.contact.colleagues.error')} />
      ) : people.length === 0 ? (
        <p className="secondary">{t('record.contact.colleagues.none')}</p>
      ) : (
        people.map((person) => (
          <div key={person.id} className="list-item">
            <span className="ava ava-lg">{initials(person.fullName)}</span>
            <div className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
              <Link to={`${listPath('sales.contact', '/contacts')}/${person.id}`}>{person.fullName}</Link>
              {person.jobTitle && <span className="t-sm secondary">{person.jobTitle}</span>}
            </div>
            {(person.mobilePhone || person.phone) && (
              <a
                className="btn btn-ghost btn-sm btn-icon"
                href={`tel:${person.mobilePhone || person.phone}`}
                aria-label={t('record.contact.callTo', { name: person.fullName })}
              >
                <Icon name="phone" size="sm" />
              </a>
            )}
            {person.email && (
              <a className="btn btn-ghost btn-sm btn-icon" href={`mailto:${person.email}`} aria-label={t('record.contact.mailTo', { name: person.fullName })}>
                <Icon name="mail" size="sm" />
              </a>
            )}
          </div>
        ))
      )}
    </Panel>
  )
}
